<template>
  <div class="student-management-panel">
    <div class="student-management-group-table-container d-flex flex-column mt-3 mb-5">
      <v-row
        v-if="showStudentManagementTestConnectionBtn"
        id="testConnectionContainer"
        align-content="center"
        align="center"
        no-gutters
      >
        <v-col cols="auto">
          <cs-button
            id="btnTestConnection"
            primary
            class="ml-2"
            name="btnTestConnection"
            :disabled="studentManagementTestConnectionInProgress"
            :loading="studentManagementTestConnectionInProgress"
            label="Test Connection"
            @click="studentManagementTestConnection()"
          ></cs-button>
        </v-col>
        <v-col self-align="left" class="pt-1">
          <v-label
            :class="
              studentManagementTestConnectionFailedStatus.length > 0 ? 'generic-error-message' : ''
            "
          >
            {{ studentManagementTestConnectionStatusDisplay }}
          </v-label>
        </v-col>
      </v-row>
      <v-row class="d-flex justify-start">
        <v-col>
          <cs-button
            id="btnClientRetrieveGroupsFromVendor"
            :primary="!(isReadFromCache && !groupsLoading)"
            name="btnClientRetrieveGroupsFromVendor"
            :loading="groupsLoading"
            label="Retrieve Groups from Vendor"
            @click="updateGroups(false)"
          ></cs-button>
          <cs-button
            id="btnClientRetrieveGroupsFromCache"
            primary
            name="btnClientRetrieveGroupsFromCache"
            label="Retrieve Groups from Cache"
            :disabled="!isReadFromCache || groupsLoading"
            @click="updateGroups(true)"
          ></cs-button>
        </v-col>
        <v-col class="d-flex justify-end">
          <cs-button
            id="btnGroupsSave"
            primary
            name="btnGroupsSave"
            label="Save"
            :disabled="saveButtonDisabled()"
            @click="openSaveConfirmation()"
          ></cs-button>
        </v-col>
      </v-row>
    </div>

    <div style="overflow: auto; height: 95vh">
      <client-student-management-group
        :group-list="groupList"
        :parent-selection-list="originalGroupList"
        :school-types="schoolTypes"
        :hide-selected="hideSelected"
        :groups-loading="groupsLoading"
        :pagination="studentGroupPaginationInfo"
        :student-management-group-sync-diffs="studentManagementGroupSyncDiffs"
        :vendor-group-id-display-enabled="vendorGroupIdDisplayEnabled"
        :search-term="searchTerm"
        class="mx-3"
        @update:group-list="onGroupListChanged"
        @update:search-term="onSearchTermChanged"
        @update:pagination="onPaginationChanged"
      />
    </div>

    <cs-form-dialog
      :model-value="groupSaveGenericDialogDisplayed"
      name="CSDialog"
      max-width="490"
      heading="Student Management"
      body-id="group-import-modal--generic-dialog"
      :primary-action="{ label: 'OK', eventName: 'primary-click' }"
      persistent
      @primary-click="genericDialogOkBtnClick"
      @update:model-value="genericDialogCloseBtnClick"
    >
      <template #cs-form-dialog-content>
        <v-spacer></v-spacer>
        <div :class="isGenericDialogDisplayedAsError ? 'generic-error-message' : ''">
          <div v-safe-html="groupSaveGenericDialogMessage"></div>
        </div>
      </template>
    </cs-form-dialog>
    <cs-form-dialog
      id="confirm-save-form-dialog"
      v-model="confirmSaveDialogDisplayed"
      heading="Student Management"
      :primary-action="{ label: 'Continue', eventName: 'primary-click' }"
      @primary-click="saveButtonClick"
    >
      <template #cs-form-dialog-content>
        <div>
          <group-mapping-summary
            :summary="groupMappingChangeSummary"
            :student-group-sync-diffs="studentManagementGroupSyncDiffs"
          />
          <span>You are about to save the group mappings. Are you sure you want to proceed?</span>
        </div>
      </template>
    </cs-form-dialog>
  </div>
</template>

<script>
import * as types from '@/store/mutationTypes';
import { CSBase } from '@complispace/cs-design-system';
import studentGroupTableColumn from '../lib/const/studentGroupTableColumn';
import { populateErrorMessage } from '../lib/errorMessages';
import studentManagement from '../lib/studentManagement';
import groupMappingChangeSummary from '../lib/groupMappingChangeSummary';
import buildStudentGroupRows from '../lib/buildStudentGroupRows';
import buildStudentGroupingPayload from '../lib/buildStudentGroupingPayload';
import dbEntityRelation from '../lib/const/dbEntityRelation';
import ClientStudentManagementGroup from './ClientStudentManagementGroup';
import GroupMappingSummary from './GroupMappingSummary';
import { cloneDeep, sortBy, isEqual } from 'lodash';

export default {
  name: 'StudentManagementPanel',

  components: {
    GroupMappingSummary,
    ClientStudentManagementGroup
  },

  extends: CSBase,

  data() {
    return {
      showStudentManagementTestConnectionBtn: false,
      studentManagementTestConnectionInProgress: false,
      studentManagementTestConnectionAt: '',
      studentManagementTestConnectionFailedStatus: '',
      isGenericDialogDisplayedAsError: false,
      isGenericDialogDisplayedReloadAfter: true,
      studentGroupPaginationInfo: {
        itemsPerPage: 30,
        page: 1,
        sortBy: []
      },
      schoolTypes: [],
      savedSchoolTypes: [],
      hideSelected: true,
      groupList: [],
      groupsLoading: false,
      clientStudentGroupSubsetAttributeKeys: ['sub_long', 'sub_short', 'sub_type'],
      groupSaveGenericDialogDisplayed: false,
      groupSaveGenericDialogMessage: '',
      groupSaveGenericDialogSuccessMessage: 'Groups have been saved.',
      querystringRefToTableColumnRefMap: {},
      tableColumnRefToQuerystringRefMap: {},
      searchTerm: '',
      originalGroupList: [],
      savedGroupManagement: [],
      schoolNameById: {},
      studentManagementGroupSyncDiffs: [],
      confirmSaveDialogDisplayed: false,
      isReadFromCache: false,
      groupMappingChangeSummary: null
    };
  },
  computed: {
    groupMappingChangeSummaryLines() {
      const { groupChanges } = groupMappingChangeSummary.getGroupMappingChangeSummaryLines(
        this.groupMappingChangeSummary
      );
      return groupChanges;
    },
    yearCountReduced() {
      return this.groupMappingChangeSummaryLines.some((line) => line.label === 'Years');
    },
    overallChanges() {
      const { overallChanges } = groupMappingChangeSummary.getGroupMappingChangeSummaryLines(
        this.groupMappingChangeSummary
      );
      return overallChanges;
    },
    vendorGroupIdDisplayEnabled() {
      return this.$store.state.common.vendorGroupIdDisplayEnabled;
    },
    studentManagementTestConnectionButtonDisabled() {
      return this.studentManagementTestConnectionInProgress;
    },
    studentManagementTestConnectionStatusDisplay() {
      if (
        this.studentManagementTestConnectionFailedStatus.length === 0 &&
        this.studentManagementTestConnectionAt.length > 0
      ) {
        return `Last Succeed At: ${this.$app
          .moment(this.studentManagementTestConnectionAt)
          .format('YYYY-MM-DD HH:mm:ss')}.`;
      }

      if (this.studentManagementTestConnectionAt.length > 0) {
        return `Last Failed At: ${this.$app
          .moment(this.studentManagementTestConnectionAt)
          .format('YYYY-MM-DD HH:mm:ss')}. ${this.studentManagementTestConnectionFailedStatus}`;
      }

      return '';
    }
  },
  watch: {
    studentGroupPaginationInfo: {
      handler(val) {
        this.syncQueryStringFromPage(val);
      },
      deep: true
    },
    '$route.query': {
      immediate: true,
      handler(newVal, oldVal) {
        if (Object.keys(newVal).length === 0 && oldVal && Object.keys(oldVal).length !== 0) {
          window.location.reload();
        }
      }
    },
    searchTerm() {
      this.resetStudentGroupPaginationInfo();
      this.syncQueryStringFromPage(this.studentGroupPaginationInfo);
    }
  },
  async mounted() {
    const { query } = this.$route;
    Object.keys(studentGroupTableColumn).forEach((key) => {
      this.querystringRefToTableColumnRefMap[studentGroupTableColumn[key].querystringRef] =
        studentGroupTableColumn[key].tableColumnRef;
      this.tableColumnRefToQuerystringRefMap[studentGroupTableColumn[key].tableColumnRef] =
        studentGroupTableColumn[key].querystringRef;
    });
    const { axiosInstance, eventPluginCommonAdapter, stbApiAdapter } = this.$app;
    const isVendorAuthScheme =
      !!this.$route.query['vendor-sid'] && !!this.$route.query['oauth-signature'];
    if (!isVendorAuthScheme) {
      await this.$store.dispatch('authorisation/validateAndSetUserTokenOrLogout', {
        query: this.$route.query,
        axiosInstance
      });
    }
    await this.$store.dispatch('common/initClientConfig', {
      queryParam: isVendorAuthScheme ? this.$route.query : {},
      eventPluginCommonAdapter,
      stbApiAdapter
    });
    const clientSid = this.$store.state.common.userClientSid;
    try {
      const data = await this.$store.dispatch('clients/fetchClientStudentManagementGrouping', {
        clientSid,
        queryParam: this.$store.state.common.apiParam
      });
      this.schoolNameById = ((data && data.schoolGroups) || []).reduce((acc, school) => {
        acc[`${school.id}`] = school.name;
        return acc;
      }, {});
      const groupRows = buildStudentGroupRows(data);
      await this.populateGroupTable(groupRows);
    } catch (e) {
      console.error('CLIENT_STUDENT_GROUPS', 'Failed to retrieve client student groups');
    }
    this.applyQueryStringToState(query);
  },
  methods: {
    async updateGroups(readFromCache) {
      this.clearSearchInput();

      const requestParam = {
        'vendor-sid': this.$store.state.common.apiParam['vendor-sid'],
        'oauth-signature': this.$store.state.common.apiParam['oauth-signature']
      };

      const getClientsArgHash = {
        sid: this.$store.state.common.userClientSid,
        requestParam,
        store: this.$store
      };
      this.groupsLoading = true;
      const cbGetClientsResponse = await this.$app.stbApiAdapter.getClients(getClientsArgHash);
      if (!cbGetClientsResponse.error) {
        const schoolTypeData = JSON.parse(
          JSON.stringify(cbGetClientsResponse.data[0].metadata.schoolType)
        );
        this.schoolTypes = [];
        if (schoolTypeData) {
          Object.keys(schoolTypeData).forEach((schoolKey) => {
            this.schoolTypes.push({
              id: schoolKey,
              name: schoolTypeData[schoolKey]
            });
          });
        }
      }

      try {
        const remoteGroupList = await this.$store.dispatch(
          'clients/fetchClientVendorStudentGroups',
          {
            clientSid: this.$store.state.common.userClientSid,
            queryParam: {
              readFromCache
            }
          }
        );

        this.isReadFromCache = true;
        const existingGroupList = JSON.parse(JSON.stringify(this.groupList));
        const existingGroupRows = JSON.parse(JSON.stringify(this.savedGroupManagement));

        this.studentManagementGroupSyncDiffs = studentManagement.getGroupSyncDiffs(
          existingGroupRows,
          remoteGroupList
        );

        this.groupList = remoteGroupList.map((remoteGroup) => {
          const existingGroupDetected = existingGroupList.find(
            (s) => `${s.id}` === `${remoteGroup.id}`
          );

          if (existingGroupDetected && existingGroupDetected.group !== remoteGroup.name) {
            existingGroupDetected.group = remoteGroup.name;
          }

          const resolvedGroup = existingGroupDetected
            ? JSON.parse(JSON.stringify(existingGroupDetected))
            : {
                id: remoteGroup.id,
                group: remoteGroup.name
              };

          this.clientStudentGroupSubsetAttributeKeys.forEach((subsetAttributeKey) => {
            const subsetAttributeValue = remoteGroup[subsetAttributeKey];

            if (typeof subsetAttributeValue !== 'undefined') {
              resolvedGroup[subsetAttributeKey] = subsetAttributeValue;
            }
          });

          return resolvedGroup;
        });

        this.originalGroupList = cloneDeep(this.groupList);
      } catch (e) {
        this.$store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, false);

        this.groupSaveGenericDialogDisplayed = true;
        this.isGenericDialogDisplayedReloadAfter = false;
        this.isGenericDialogDisplayedAsError = true;
        this.groupSaveGenericDialogMessage = 'Failed to retrieve client student groups.';
      } finally {
        this.groupsLoading = false;
      }
    },
    onGroupListChanged(newGroupList) {
      this.groupList = newGroupList;
    },
    onSearchTermChanged(newSearchTerm) {
      this.searchTerm = newSearchTerm;
    },
    onPaginationChanged(newPagination) {
      if (isEqual(this.studentGroupPaginationInfo, newPagination)) {
        return;
      }
      this.studentGroupPaginationInfo = newPagination;
    },
    saveButtonDisabled() {
      return this.groupList.length === 0;
    },

    async previewGroupMappingChanges() {
      const payload = buildStudentGroupingPayload(
        this.groupList,
        this.schoolNameById,
        this.clientStudentGroupSubsetAttributeKeys
      );

      const clientSid = this.$store.state.common.userClientSid;

      try {
        const response = await this.$store.dispatch(
          'clients/patchClientStudentManagementGrouping',
          {
            clientSid,
            payload: {
              ...payload,
              clientSelfMadeChanges: true
            }
          }
        );
        return response?.error ? null : response;
      } catch (err) {
        console.error('error validating grouping change summary', err?.message);
        return null;
      }
    },

    async openSaveConfirmation() {
      const changeSummary = await this.previewGroupMappingChanges();

      this.groupMappingChangeSummary = changeSummary;

      if (changeSummary && !changeSummary.hasChanges) {
        this.saveButtonClick();
        return;
      }

      this.confirmSaveDialogDisplayed = true;
    },
    async saveButtonClick() {
      this.confirmSaveDialogDisplayed = false;
      const clientSid = this.$store.state.common.userClientSid;

      const payload = buildStudentGroupingPayload(
        this.groupList,
        this.schoolNameById,
        this.clientStudentGroupSubsetAttributeKeys
      );

      const groupingResponse = await this.$store.dispatch(
        'clients/patchClientStudentManagementGrouping',
        {
          clientSid,
          payload: {
            ...payload,
            clientSelfMadeChanges: true,
            confirmedSave: true
          }
        }
      );

      if (!groupingResponse?.error) {
        this.originalGroupList = cloneDeep(this.groupList);
        this.savedGroupManagement = cloneDeep(this.groupList);
        this.groupSaveGenericDialogMessage = this.groupSaveGenericDialogSuccessMessage;
        this.groupSaveGenericDialogDisplayed = true;
      } else {
        let dialogMessage = 'Group cannot be saved.';
        const errorMessage =
          groupingResponse.error?.message || groupingResponse.error?.refinedMessage;

        if (errorMessage) {
          dialogMessage = `${dialogMessage} ${errorMessage}.`;
        }

        this.groupSaveGenericDialogMessage = populateErrorMessage({
          message: dialogMessage,
          store: this.$store
        });
        this.groupSaveGenericDialogDisplayed = true;
      }
      this.clearSearchInput();
    },
    async genericDialogOkBtnClick() {
      this.groupSaveGenericDialogDisplayed = false;
      this.isGenericDialogDisplayedAsError = false;
      if (this.isGenericDialogDisplayedReloadAfter) {
        this.$app.window.location.reload();
      } else {
        this.isGenericDialogDisplayedReloadAfter = true;
      }
    },
    async genericDialogCloseBtnClick(boolShow) {
      if (!boolShow) {
        this.groupSaveGenericDialogDisplayed = false;
        this.isGenericDialogDisplayedAsError = false;
        if (this.isGenericDialogDisplayedReloadAfter) {
          this.$app.window.location.reload();
        } else {
          this.isGenericDialogDisplayedReloadAfter = true;
        }
      }
    },

    async populateGroupTable(groupRows) {
      this.groupsLoading = true;
      const requestParam = {
        'vendor-sid': this.$store.state.common.apiParam['vendor-sid'],
        'oauth-signature': this.$store.state.common.apiParam['oauth-signature']
      };

      if (!this.$store.state.common.userClientSid) {
        this.groupsLoading = false;
        return;
      }
      const getClientsArgHash = {
        sid: this.$store.state.common.userClientSid,
        requestParam,
        store: this.$store
      };
      const cbGetClientsResponse = await this.$app.stbApiAdapter.getClients(getClientsArgHash);
      if (!cbGetClientsResponse.error) {
        const schoolTypeData = JSON.parse(
          JSON.stringify(cbGetClientsResponse.data[0].metadata.schoolType)
        );
        this.schoolTypes = [];
        if (schoolTypeData) {
          Object.keys(schoolTypeData).forEach((schoolKey) => {
            this.schoolTypes.push({
              id: schoolKey,
              name: schoolTypeData[schoolKey]
            });
          });
        }
        const client = cbGetClientsResponse.data[0];
        const vendors = client[dbEntityRelation.VENDOR_LIST];
        const activeStudentManagementVendorSid = client.metadata?.activeStudentManagementVendorSid;
        if (activeStudentManagementVendorSid) {
          const vendor = vendors.find((v) => v.sid === `${activeStudentManagementVendorSid}`);
          if (vendor?.join_metadata?.studentManagement) {
            this.isReadFromCache = vendor.join_metadata.studentManagement.isStudentGroupListCached;
            this.studentManagementTestConnectionAt =
              vendor.join_metadata.studentManagement.testConnectionAt || '';
            this.studentManagementTestConnectionFailedStatus =
              vendor.join_metadata.studentManagement.testConnectionFailedStatus || '';
          }
        }
        this.showStudentManagementTestConnectionBtn =
          !!client.computed?.showStudentManagementTestConnectionBtn;
      }

      this.groupList = sortBy(cloneDeep(groupRows || []), (o) => o.group);
      this.originalGroupList = cloneDeep(this.groupList);
      this.savedGroupManagement = cloneDeep(this.groupList);
      this.$forceUpdate();
      this.groupsLoading = false;
    },
    syncQueryStringFromPage(paginationOptions) {
      const searchQuery = {
        page: paginationOptions.page,
        length: paginationOptions.itemsPerPage
      };
      if (paginationOptions.sortBy.length !== 0 && paginationOptions.sortBy[0] !== '') {
        searchQuery.order = paginationOptions.sortBy[0].key;
        if (paginationOptions.sortBy[0].order) {
          searchQuery.direction = paginationOptions.sortBy[0].order.toUpperCase();
        }
      }

      if (this.groupList.length === 0) {
        delete searchQuery.page;
        delete searchQuery.length;
        delete searchQuery.direction;
        delete searchQuery.order;
      }

      if (this.searchTerm && String(this.searchTerm).trim().length !== 0) {
        searchQuery.s = String(this.searchTerm).trim().toLocaleLowerCase();
      }

      this.$router.replace({ query: searchQuery }, () => {});
    },
    applyQueryStringToState(query) {
      const searchQuery = {};
      const {
        page: parsedPage,
        length: parsedLength,
        direction: parsedDirection,
        order: parsedSortBy,
        s: parsedSearchGroup
      } = query;

      if (parsedSearchGroup && String(parsedSearchGroup).trim().length !== 0) {
        this.searchTerm = String(parsedSearchGroup).trim().toLowerCase();
        searchQuery.s = this.searchTerm;
      }

      if (parsedLength === '10' || parsedLength === '30' || parsedLength === '50') {
        this.studentGroupPaginationInfo.itemsPerPage = parseInt(parsedLength, 10);
        searchQuery.length = parseInt(parsedLength, 10);
      } else {
        this.studentGroupPaginationInfo.itemsPerPage = 30;
        searchQuery.length = 30;
      }

      if (!Number.isNaN(parseInt(parsedPage, 10)) && parseInt(parsedPage, 10) > 0) {
        this.studentGroupPaginationInfo.page = parseInt(parsedPage, 10);
        searchQuery.page = parseInt(parsedPage, 10);
      } else {
        this.studentGroupPaginationInfo.page = 1;
        searchQuery.page = 1;
      }
      if (
        studentGroupTableColumn.GROUP.querystringRef === parsedSortBy ||
        studentGroupTableColumn.SCHOOL_TYPE.querystringRef === parsedSortBy ||
        studentGroupTableColumn.PARENT.querystringRef === parsedSortBy ||
        studentGroupTableColumn.SUBJECT_GROUP.querystringRef === parsedSortBy ||
        studentGroupTableColumn.CUSTOM_GROUP.querystringRef === parsedSortBy
      ) {
        this.studentGroupPaginationInfo.sortBy[0] = { key: parsedSortBy };
        searchQuery.order = parsedSortBy;
        if (parsedDirection === 'DESC' || parsedDirection === 'ASC') {
          this.studentGroupPaginationInfo.sortBy[0].order = parsedDirection.toLowerCase();
          searchQuery.direction = parsedDirection;
        } else {
          searchQuery.direction = 'ASC';
        }
      }
      if (this.groupList.length === 0) {
        delete searchQuery.page;
        delete searchQuery.length;
        delete searchQuery.direction;
        delete searchQuery.order;
      }
      this.$router.replace({ query: searchQuery }, () => {});
    },
    clearSearchInput() {
      this.searchTerm = '';
      const { query } = this.$route;
      delete query.s;
      const searchQuery = JSON.parse(JSON.stringify(query));
      this.$router.replace({ query: {} }, () => {});
      this.$router.replace({ query: { ...searchQuery } }, () => {});
      this.$forceUpdate();
    },
    resetStudentGroupPaginationInfo() {
      if (this.studentGroupPaginationInfo.itemsPerPage !== 30) {
        this.studentGroupPaginationInfo.itemsPerPage = 30;
      }
      if (this.studentGroupPaginationInfo.page !== 1) {
        this.studentGroupPaginationInfo.page = 1;
      }
      if (this.studentGroupPaginationInfo.sortBy && this.studentGroupPaginationInfo.sortBy[0]) {
        this.studentGroupPaginationInfo.sortBy = [];
      }
    },
    async studentManagementTestConnection() {
      this.studentManagementTestConnectionInProgress = true;

      const result = await this.$app.stbApiAdapter.getClientStudentTestConnection();

      this.studentManagementTestConnectionInProgress = false;

      this.$store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, false);
      this.groupSaveGenericDialogDisplayed = true;
      this.isGenericDialogDisplayedReloadAfter = false;

      if (!result.error) {
        this.studentManagementTestConnectionAt = result.data.connectionTestedAt;
        this.studentManagementTestConnectionFailedStatus = '';
        this.isGenericDialogDisplayedAsError = false;
        this.groupSaveGenericDialogMessage = `Successfully connect with student management vendor for school "${result.data.schoolName}".`;
      } else {
        this.studentManagementTestConnectionAt = this.$app.moment().toISOString();
        this.studentManagementTestConnectionFailedStatus = result.message;
        this.isGenericDialogDisplayedAsError = true;
        this.groupSaveGenericDialogMessage = result.message;
      }
    }
  }
};
</script>

<style scoped>
.group-mapping-change-summary-table {
  margin-bottom: 10px;
}

.student-management-panel {
  margin-left: 50px;
  margin-right: 50px;
  margin-top: 30px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: fit-content;
  background-color: #fff;
}

.student-management-panel-student-select {
  margin-top: 10px !important;
  margin-bottom: 5px !important;
}

.student-management-group-search-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  max-height: 100px;
  margin-top: 8px;
}

#student-management-search-input {
  width: 300px;
  padding-left: 4px;
}

.studentGroupId {
  color: gray;
}

.row-select-checkbox {
  padding-left: 8px !important;
}

.generic-error-message {
  color: rgb(var(--v-theme-error));
  text-align: left;
}

.checkbox-options {
  max-width: 150px;
  padding-bottom: 8px;
}

.student-management-panel-parent-select {
  width: 300px;
  height: 35px;
  margin-top: 6px !important;
  float: left;
}

.student-management-panel-parent {
  color: rgb(var(--v-theme-primary)) !important;
}

.student-management-panel :deep(.table-header) {
  font-weight: 700 !important;
  font-size: 12px !important;
  line-height: 18px !important;
  white-space: nowrap;
}

.student-management-panel :deep(thead > tr > th > .mdi-checkbox-marked) {
  color: red !important;
}
</style>
